// React

import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import PropTypes, { object } from "prop-types";
import { useCallback, useContext, useEffect, useState } from "react";

// @mui material components
import { Card, Collapse, Fade, Icon, Menu, MenuItem, Modal, Select, Tooltip } from "@mui/material";

import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import ModalDelete from "components/Modals/Delete";
import CustomPagination from "components/Pagination";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DataTable from "components/Tables/DataTable";
import { MessageManager } from "context";
import proveedoresData from "../data/proveedoresData";
import ModalAddProveedor from "./ModalAdd";
import ModalDocumentacion from "./Documentacion";
import { getUproColor } from "utils/colors";
import SoftButton from "components/SoftButton";
import * as XLSX from "xlsx";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "70%", xxl: "60%" },
  overflowY: "auto",
};

const GET_PROVEEDORES = gql`
  query getProveedores($filter: JSON, $search: String, $pagination: PaginationInput, $order: Order) {
    paginationInfo {
      pages
      total
    }
    proveedores(
      filter: $filter
      search: $search
      pagination: $pagination
      order: $order
    ) {
      id
      tipoProveedor
      razonSocial
      nombre
      apellido
      tipoDocumento
      documento
      tipoResponsable
      documentosPendientes
      habilitado
      banco
      cbu
      alias
      titular
      telefono
        telefonoSecundario
        referenciaContacto
      localidad {
        id
        nombre
        provincia {
          id
          nombre
        }
      }
      categorias {
        id
      }
      user {
        email
      }
    }
  }
`;

const DELETE_PROVEEDOR = gql`
  mutation deleteProveedor($id: ID!) {
    deleteProveedor(id: $id) {
      id
    }
  }
`;

const tipoResponsables = {
  1: "IVA Responsable Inscripto",
  2: "IVA Responsable no Inscripto",
  3: "IVA no Responsable",
  4: "IVA Sujeto Exento",
  5: "Consumidor Final",
  6: "Responsable Monotributo",
  7: "Sujeto no Categorizado",
  8: "Proveedor del Exterior",
  9: "Cliente del Exterior",
  10: "IVA Liberado – Ley Nº 19.640",
  11: "IVA Responsable Inscripto – Agente de Percepción",
  12: "Pequeño Contribuyente Eventual",
  13: "Monotributista Social",
  14: "Pequeño Contribuyente Eventual Social",
};

const tipoDocumentos = {
  80: "CUIT",
  86: "CUIL",
  96: "DNI",
  99: "Consumidor final",
};

export default function ModalProveedores({ open, handleClose, onPendientesDetected, readOnly = false }) {
  const [proveedor, setProveedor] = useState();
  const [rows, setRows] = useState([]);
  const [openMenu, setOpenMenu] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState();
  const { columns, getRows } = proveedoresData;
  const { handleSnackbar } = useContext(MessageManager);
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useState(1);
  const [collapse, setCollapse] = useState(true);
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "nombre",
    orden: "ASC",
  });
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [openModalDocumentacion, setOpenModalDocumentacion] = useState(false);
  const [filter, setFilter] = useState({
    estado: "pendientes",
    rubro: "todos",
    gqlFilter: { _documentos: { _required: true, estado: "Pendiente" } },
  });

  const { data: rubros } = useQuery(gql`
    query GetRubros {
      categoriasMateriaPrima(order: { field: "nombre", order: ASC }) {
        id
        nombre
      }
    }  
  `);

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setPage(1);
      setSearch(event.target.value);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const [getProveedoresDownload] = useLazyQuery(gql`
    query getProveedores($filter: JSON, $search: String, $order: Order) {
      proveedores(
        filter: $filter
        search: $search
        order: $order
      ) {
        id
        tipoProveedor
        razonSocial
        nombre
        apellido
        tipoDocumento
        documento
        tipoResponsable
        habilitado
        telefono
        telefonoSecundario
        referenciaContacto
        localidad {
          id
          nombre
          provincia {
            id
            nombre
          }
        }
        categorias {
          id
        }
        user {
          email
        }
      }
    }  
  `);

  const [getProveedores, { loading, data: proveedores, refetch }] = useLazyQuery(GET_PROVEEDORES, {
    variables: {
      search: search || null,
      pagination: {
        page: page,
        limit: 10,
      },
      order: { field: orderBy.campo, order: orderBy.orden },
      filter: filter.gqlFilter,
    },
    fetchPolicy: "network-only",
    cachePolicy: "no-cache",
  });
  const [deleteProveedor] = useMutation(DELETE_PROVEEDOR);

  const { data: provincias } = useQuery(gql`
    query GetProvincias {
      provincias {
        id
        nombre
      }
    }
  `);

  const [getLocalidades, { data: localidades }] = useLazyQuery(gql`
    query GetLocalidades($provincia: ID!) {
      localidades(provincia: $provincia) {
        id
        nombre
        provincia {
          id
        }
      }
    }
  `);

  const handleDelete = (id) => {
    deleteProveedor({
      variables: {
        id,
      },
    })
      .then(() => {
        getProveedores();
        handleSnackbar("Proveedor eliminado correctamente", "success");
      })
      .catch(() => {
        handleSnackbar("Error al eliminar el proveedor", "error");
      });
  };

  const handleOpenMenu = (event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedItem(item);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpenMenu(false);
  };

  const handleFilter = useCallback((e) => {
    const { name, value } = e.target;
    let gqlFilter = filter.gqlFilter ?? {};

    switch (name) {
      case "estado":
        gqlFilter = {
          ...gqlFilter,
          _documentos: value !== "todos" ? { _required: true, estado: "Pendiente" } : undefined,
        }
        break;
      case "rubro":
        gqlFilter = {
          ...gqlFilter,
          idsCategorias: value !== "todos" ? { _substring: `"${value}"` } : undefined,
        };
        break;
      default:
        break;
    };

    if (Object.keys(gqlFilter).length === 0) gqlFilter = undefined;

    setFilter(prev => ({
      ...prev,
      [name]: value,
      gqlFilter,
    }));
  }, [filter]);

  const handleDescargar = async () => {
    //en la primera fila guardar filtros usados
    //luego la tabla con los proveedores

    const { data: proveedoresData } = await getProveedoresDownload({
      variables: {
        search: search || null,
        order: { field: orderBy.campo, order: orderBy.orden },
        filter: filter.gqlFilter,
      },
    });

    const title = "PROVEEDORES";
    const parameters = [
      ["Filtros aplicados"],
      ["Estado", filter.estado === "todos" ? "Todos" : "Documentos pendientes"],
      ["Rubro", filter.rubro === "todos" ? "Todos" : rubros?.categoriasMateriaPrima?.find(rubro => rubro.id === filter.rubro)?.nombre],
    ];

    // Convertir el JSON en formato de tabla
    const jsonToSheet = XLSX.utils.json_to_sheet(proveedoresData.proveedores.map(p => ({
      "ID": p.id,
      "Tipo de proveedor": "Persona " + p.tipoProveedor,
      "Razón social": p.razonSocial,
      "Nombre": p.nombre,
      "Apellido": p.apellido,
      "Tipo de documento": tipoDocumentos[p.tipoDocumento],
      "Documento": p.documento,
      "Tipo de responsable": tipoResponsables[p.tipoResponsable],
      "Habilitado": p.habilitado ? "Sí" : "No",
      "Localidad": p.localidad?.nombre,
      "Provincia": p.localidad?.provincia?.nombre,
      "Email": p.user?.email,
      "Teléfono 1": p.telefono,
      "Teléfono 2": p.telefonoSecundario,
      "Referencia de contacto": p.referenciaContacto,
      "Rubros": p.categorias?.map(c => c.id).join(", "),
    })));

    // Agregar título y parámetros
    const titleRow = [[title]];
    const emptyRow = [[]]; // Espacio entre título y parámetros
    const parametersRows = parameters;

    // Combinar todas las secciones
    const combinedData = [
      ...titleRow,
      ...emptyRow,
      ...parametersRows,
      [],
      ...XLSX.utils.sheet_to_json(jsonToSheet, { header: 1 }),
    ];

    // Crear una nueva hoja
    const ws = XLSX.utils.aoa_to_sheet(combinedData);

    // Crear el libro de trabajo
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reporte");

    //add categorias to second sheet
    const categoriasSheet = XLSX.utils.json_to_sheet(rubros?.categoriasMateriaPrima.map(c => ({
      "ID": c.id,
      "Nombre": c.nombre,
    })));
    XLSX.utils.book_append_sheet(wb, categoriasSheet, "Rubros");

    // Exportar el archivo Excel
    XLSX.writeFile(wb, "Proveedores.xlsx");
  };

  const renderMenu = (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      <MenuItem
        onClick={() => {
          setOpenModalEdit(true);
          setProveedor({
            tipoProveedor: selectedItem.tipoProveedor,
            nombre: selectedItem.nombre,
            apellido: selectedItem.apellido,
            tipoDocumento: selectedItem.tipoDocumento,
            documento: selectedItem.documento,
            tipoResponsable: selectedItem.tipoResponsable,
            razonSocial: selectedItem.razonSocial,
            provincia: selectedItem.localidad?.provincia?.id,
            localidad: selectedItem.localidad?.id,
            id: selectedItem.id,
            idsCategorias: selectedItem.categorias?.map((c) => c.id) || [],
            user: selectedItem.user,
            telefono: selectedItem.telefono,
            telefonoSecundario: selectedItem.telefonoSecundario,
            referenciaContacto: selectedItem.referenciaContacto,
            cbu: selectedItem.cbu,
            banco: selectedItem.banco,
            alias: selectedItem.alias,
            titular: selectedItem.titular,
          });
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <SoftBox>
            <SoftTypography variant="p">Editar</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                color: getUproColor("uproYellow"),
              }}
            >
              edit
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
      <MenuItem
        onClick={() => {
          setOpenModalDocumentacion(true);
          setAnchorEl(null);
          setOpenMenu(false);
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <SoftBox>
            <SoftTypography variant="p">Documentación</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                color: getUproColor("uproGreen"),
              }}
            >
              description
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
      {!readOnly && (
        <MenuItem
          onClick={() => {
            setOpenModalDelete(true);
            handleCloseMenu();
          }}
        >
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              width: "100%",
            }}
          >
            <SoftBox>
              <SoftTypography variant="p">Eliminar</SoftTypography>
            </SoftBox>
            <SoftBox>
              <Icon
                fontSize="small"
                sx={{
                  verticalAlign: "middle",
                  color: getUproColor("uproPink"),
                }}
              >
                delete
              </Icon>
            </SoftBox>
          </SoftBox>
        </MenuItem>
      )}
    </Menu>
  );

  useEffect(() => {
    setProveedor();
    getProveedores();
    setSearch("");
  }, [open]);

  useEffect(() => {
    if (proveedores && proveedores?.proveedores) {
      const pendientes = proveedores?.proveedores?.some(p => p.documentosPendientes > 0);
      setRows(getRows(proveedores.proveedores, handleOpenMenu));
      setPagesInfo(proveedores?.paginationInfo);
      if (onPendientesDetected) {
        onPendientesDetected(pendientes);
      }
    }
  }, [proveedores]);

  useEffect(() => {
    if (proveedor?.provincia) {
      getLocalidades({ variables: { provincia: proveedor?.provincia } }).then((data) => {
        // Si la localidad no pertenece a la provincia seleccionada, se limpia el campo
        if (!data?.data?.localidades?.find((localidad) => localidad.id === proveedor?.localidad)) {
          setProveedor({
            ...proveedor,
            localidad: -1,
          });
        }
      });
    }
  }, [proveedor?.provincia]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" p={3} pb={0}>
              <SoftBox>
                <SoftTypography variant="h6">Proveedores</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="end" alignItems="center" gap={1}>
                <SoftBox>
                  <SoftInput
                    placeholder="Escribe aquí..."
                    icon={{ component: "search", direction: "left" }}
                    onChange={handleSearch}
                  />
                </SoftBox>
                <SoftBox>
                  <Tooltip placement="top" title="Opciones">
                    <SoftButton
                      variant="gradient"
                      color="uproGreen"
                      iconOnly
                      circular
                      onClick={() => setCollapse(!collapse)}
                    >
                      <Icon sx={{ fontWeight: "regular" }}>{collapse ? "filter_alt_off" : "filter_alt"}</Icon>
                    </SoftButton>
                  </Tooltip>
                </SoftBox>
                <SoftBox>
                  <Tooltip placement="top" title="Cerrar">
                    <SoftButton
                      variant="gradient"
                      color="error"
                      iconOnly
                      circular
                      onClick={handleClose}
                    >
                      <Icon sx={{ fontWeight: "regular" }}>close</Icon>
                    </SoftButton>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <Collapse in={collapse}>
              <SoftBox display="flex" gap={1} px={3}>
                <SoftBox>
                  <Select name="estado" value={filter.estado} onChange={handleFilter}>
                    <MenuItem value="todos">
                      <SoftBox display="flex" gap={1}>
                        <Icon fontSize="small">group</Icon>
                        Todos
                      </SoftBox>
                    </MenuItem>
                    <MenuItem value="pendientes">
                      <SoftBox display="flex" gap={1}>
                        <Icon fontSize="small" sx={{ color: getUproColor("uproPink") }}>
                          new_releases
                        </Icon>
                        Documentos pendientes
                      </SoftBox>
                    </MenuItem>
                  </Select>
                </SoftBox>
                <SoftBox>
                  <Select
                    name="rubro"
                    value={filter.rubro}
                    onChange={handleFilter}
                  >
                    <MenuItem value="todos">
                      <SoftBox display="flex" gap={1}>
                        <Icon fontSize="small">category</Icon>
                        Rubros
                      </SoftBox>
                    </MenuItem>
                    {rubros?.categoriasMateriaPrima?.map((rubro, index) => (
                      <MenuItem key={index} value={rubro.id} cla>
                        <SoftBox display="flex" gap={1}>
                          <Icon fontSize="small" sx={{ color: getUproColor("uproYellow") }}>category</Icon>
                          {rubro.nombre}
                        </SoftBox>
                      </MenuItem>
                    ))}
                  </Select>
                </SoftBox>
                <SoftButton color="uproGreen" circular onClick={handleDescargar}>
                  <Icon>download</Icon>
                  Descargar planilla
                </SoftButton>
              </SoftBox>
            </Collapse>
            <SoftBox
              sx={{
                maxHeight: "50vh",
                overflow: "auto",
              }}
            >
              {loading ? (
                <Loading />
              ) : proveedores?.proveedores?.length > 0 ? (
                <DataTable columns={columns} rows={rows} order={orderBy} setOrder={handleOrderBy} />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={proveedores?.proveedores?.length}
              total={pagesInfo?.total}
              pages={pagesInfo?.pages}
              page={page}
              setPage={setPage}
              noCard
            />
            {renderMenu}
          </SoftBox>
          <ModalAddProveedor
            open={openModalEdit}
            handleClose={() => {
              setOpenModalEdit(false);
              setProveedor();
            }}
            data={proveedor}
            refetch={refetch}
            readOnly={readOnly}
          />
          <ModalDelete
            open={openModalDelete}
            handleClose={() => setOpenModalDelete(false)}
            nombre={`el proveedor ${selectedItem?.razonSocial || selectedItem?.nombre + " " + selectedItem?.apellido
              }`}
            functionDelete={() => {
              handleDelete(selectedItem?.id);
            }}
          />
          <ModalDocumentacion
            open={openModalDocumentacion}
            handleClose={() => setOpenModalDocumentacion(false)}
            idProveedor={selectedItem?.id}
            readOnly={readOnly}
          />
        </Card>
      </Fade>
    </Modal>
  );
}

ModalProveedores.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  onPendientesDetected: PropTypes.func,
  readOnly: PropTypes.bool,
};
